/**
 * @file OrderItemEditRow.tsx
 * @description Строка состава заказа: просмотр и режим правки
 * (phase19.15 шаг 4, требование пользователя 2026-08-15).
 *
 * Одна строка на оба режима: в просмотре — количество и суммы, в правке —
 * степпер и удаление. Разводить два компонента незачем — разошлись бы подписи
 * и раскладка на 360px, где строка складывается в столбик.
 *
 * Позиция-услуга «Доставка» контролов не получает никогда: у неё свой источник
 * цены (калькулятор или ручная сумма в блоке заявки).
 *
 * Позиция без товара каталога (OQ-54) — проблема, а не мелочь: строка красится
 * тоном `danger` и несёт стикер «Нужно решение» с действием «Привязать товар».
 * Признак считает реестр `lib/order-item-link.ts` — тот же, что у полосы
 * «Сейчас», иначе вкладка и шапка говорили бы разное.
 */

import { Minus, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { OrderItemUnlinkedNotice } from "./OrderItemUnlinkedNotice";
import {
  formatOrderItemQuantity,
  isDeliveryServiceItem,
  orderItemName,
  type OrderItemRawDto,
} from "../../lib/api-order-items";
import { formatMoney } from "../../lib/api-orders";
import { isUnlinkedOrderItem } from "../../lib/order-item-link";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { cn } from "@/lib/utils";

interface Props {
  item: OrderItemRawDto;
  /** Режим правки состава (кнопки количества и удаления). */
  editing: boolean;
  /** Идёт запрос по этой или соседней строке — блокируем контролы. */
  busy: boolean;
  /** Право SHOP_MANAGE_ORDERS: показывать ли «Привязать товар». */
  canRelink: boolean;
  /** Строка подсвечена deep-link'ом `?tab=items&focus=<id>` (на ~2 секунды). */
  focused?: boolean;
  onQuantity: (item: OrderItemRawDto, quantity: number) => void;
  onRemove: (item: OrderItemRawDto) => void;
  onRelink: (item: OrderItemRawDto) => void;
}

export function OrderItemEditRow({
  item,
  editing,
  busy,
  canRelink,
  focused = false,
  onQuantity,
  onRemove,
  onRelink,
}: Props) {
  const isService = isDeliveryServiceItem(item);
  const unlinked = isUnlinkedOrderItem({ id: item.id, skuId: item.skuId, isService });
  const [draft, setDraft] = useState(formatOrderItemQuantity(item.quantity));

  // Количество пришло с сервера (перечитали список после мутации) — локальный
  // черновик обязан за ним следовать, иначе поле «залипнет» на старом числе.
  useEffect(() => {
    setDraft(formatOrderItemQuantity(item.quantity));
  }, [item.quantity]);

  const quantity = Number(item.quantity);
  const controls = editing && !isService;

  function commit(next: number): void {
    if (!Number.isFinite(next) || next <= 0) {
      setDraft(formatOrderItemQuantity(item.quantity));
      return;
    }
    if (Math.abs(next - quantity) < 0.0005) return;
    onQuantity(item, Math.round(next * 1000) / 1000);
  }

  return (
    <li
      // `data-row-id` — общий контракт подсветки deep-link (`useFocusedRow`).
      data-row-id={item.id}
      data-unlinked={unlinked || undefined}
      className={cn(
        "flex flex-col gap-2 p-4",
        // Тон danger на всю строку: проблему видно до чтения текста, но текст
        // и стикер всё равно есть — цвет не единственный признак.
        unlinked && "bg-destructive/5",
        focused && "ring-2 ring-inset ring-primary",
      )}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium break-words">{orderItemName(item)}</span>
            {isService ? (
              <Badge variant="secondary" className="text-[10px]">
                Услуга
              </Badge>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-on-surface-variant">
            {item.productSnapshot.skuCode ? (
              <span className="font-mono">SKU: {item.productSnapshot.skuCode}</span>
            ) : null}
            {item.productSnapshot.gtin ? (
              <span className="font-mono">GTIN: {item.productSnapshot.gtin}</span>
            ) : null}
            {item.markingDatamatrix ? (
              <Badge variant="secondary" className="text-[10px]">
                DataMatrix
              </Badge>
            ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 sm:justify-end">
          {controls ? (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                className="size-12"
                aria-label="Уменьшить количество"
                disabled={busy}
                onClick={() => commit(quantity - 1)}
              >
                <Minus className="size-4" />
              </Button>
              <Input
                className="h-12 w-16 text-center"
                inputMode="decimal"
                value={draft}
                disabled={busy}
                aria-label="Количество"
                onChange={(e) => setDraft(e.target.value)}
                onBlur={() => commit(Number(draft.replace(",", ".")))}
              />
              <Button
                variant="outline"
                className="size-12"
                aria-label="Увеличить количество"
                disabled={busy}
                onClick={() => commit(quantity + 1)}
              >
                <Plus className="size-4" />
              </Button>
            </div>
          ) : (
            <span className="text-sm text-on-surface-variant whitespace-nowrap">
              {formatOrderItemQuantity(item.quantity)} × {formatMoney(item.priceGross)}
            </span>
          )}

          <div className="text-right">
            <div className="font-semibold text-primary whitespace-nowrap">
              {formatMoney(item.totalGross)} ₽
            </div>
            {Number(item.discountAmount) > 0 ? (
              <div className="text-[10px] text-on-surface-variant">
                Скидка: {formatMoney(item.discountAmount)}
              </div>
            ) : null}
          </div>

          {controls ? (
            <Button
              variant="ghost"
              className="size-12 text-destructive hover:bg-destructive/10"
              aria-label="Удалить позицию"
              disabled={busy}
              onClick={() => onRemove(item)}
            >
              <Trash2 className="size-4" />
            </Button>
          ) : null}
        </div>
      </div>

      {unlinked ? (
        <OrderItemUnlinkedNotice
          canRelink={canRelink}
          busy={busy}
          onRelink={() => onRelink(item)}
        />
      ) : null}
    </li>
  );
}
